import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box } from '@mui/material';

import routeConfiguration from '../../routeConfiguration';
import { pathByRouteName } from '../../util/routes';
import InlineTextButton from '../BasicUI/Button/InlineTextButton';
import LayoutSingleColumn from '../Layouts/LayoutSingleColumn';
import LayoutWrapperMain from '../Layouts/LayoutWrapperMain';
import LayoutWrapperTopbar from '../Layouts/LayoutWrapperTopbar/LayoutWrapperTopbar';
import Logo from '../Logo/VanSite/Logo';

const topbarSx = (theme) => ({
  // Size
  width: '100%',
  height: theme.tokens.topbarHeight,

  // Layout for child components
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  // fill
  backgroundColor: theme.tokens.matterColorLight,

  // shadows
  boxShadow: theme.tokens.boxShadow,

  [theme.breakpoints.up('lg')]: {
    height: theme.tokens.topbarHeightDesktop,
    justifyContent: 'center',
  },
});

const logoMobileSx = (theme) => ({
  display: 'block',
  '& path': {
    fill: theme.tokens.marketplaceColor,
  },
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
});

const logoDesktopSx = (theme) => ({
  display: 'none',
  width: 'auto',
  height: '27px',
  [theme.breakpoints.up('lg')]: {
    display: 'block',
  },
});

const rootSx = {
  // Expand to the available space
  flexGrow: 1,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const contentSx = (theme) => ({
  maxWidth: '587px',
  width: '80%',
  marginTop: '60px',
  marginBottom: '60px',

  [theme.breakpoints.up('md')]: {
    marginTop: '10vh',
  },
});

const numberSx = (theme) => ({
  ...theme.mixins.marketplaceHeroTitleFontStyles,
  textAlign: 'center',
  color: theme.tokens.marketplaceColor,
  margin: '1px 0 0 0',

  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceHeroTitleFontStyles['@media (min-width:900px)'],
    margin: '0',
  },
});

const headingSx = (theme) => ({
  ...theme.mixins.marketplaceModalTitleStyles,
  textAlign: 'center',
  marginTop: '16px',

  [theme.breakpoints.up('md')]: {
    ...theme.mixins.marketplaceModalTitleStyles['@media (min-width:900px)'],
    marginTop: '22px',
  },
});

const descriptionSx = (theme) => ({
  textAlign: 'center',
  marginBottom: 0,

  [theme.breakpoints.up('md')]: {
    marginTop: '24px',
    marginBottom: 0,
  },
});

export const LoadableComponentErrorBoundaryPage = () => {
  const { locale } = useIntl();
  const landingPagePath = pathByRouteName('LandingPage', routeConfiguration(), {}, locale);
  const handleOnClick = () => {
    if (typeof window !== 'undefined') {
      window.location = landingPagePath;
    }
  };

  const landingPageLink = (
    <InlineTextButton onClick={handleOnClick}>
      <FormattedMessage id="LoadableComponentErrorBoundaryPage.landingPageLink" />
    </InlineTextButton>
  );

  return (
    <div>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          {' '}
          <Box sx={topbarSx}>
            <InlineTextButton onClick={handleOnClick}>
              <Logo format="mobile" sx={logoMobileSx} />
              <Logo format="desktop" sx={logoDesktopSx} />
            </InlineTextButton>
          </Box>
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <Box sx={rootSx}>
            <Box sx={contentSx}>
              <Box sx={numberSx}>404</Box>
              <Box component="h1" sx={headingSx}>
                <FormattedMessage id="LoadableComponentErrorBoundaryPage.heading" />
              </Box>
              <Box component="p" sx={descriptionSx}>
                <FormattedMessage
                  id="LoadableComponentErrorBoundaryPage.description"
                  values={{ link: landingPageLink }}
                />
              </Box>
            </Box>
          </Box>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </div>
  );
};
